import { useState, useId, useRef } from "react";
import { useForm, useWatch, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { PlusCircle, Loader2, X, Search, AlertCircle, MapPin } from "lucide-react";
import { useDebounce } from "use-debounce";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { VirtualScroller } from "@/components/common/VirtualScroller";
import { VirtualSelectField } from "@/components/common/VirtualSelectField";
import { useAuth } from "@/hooks/useAuth";
import { regionApi, locationApi } from "@/lib/api/admin-api";
import { companyApi } from "@/lib/api/services/company.api";
import type { Company } from "@/types/company";
import type { Location } from "@/types/location";

// ─── Schema ───────────────────────────────────────────────────────────────────

const regionFormSchema = z.object({
  name: z.string().min(2, t`Region name must be at least 2 characters`),
  description: z.string().max(300).optional(),
  isActive: z.boolean(),
  companyId: z.string().optional(),
});

type RegionFormValues = z.infer<typeof regionFormSchema>;

// ─── Layout helpers ───────────────────────────────────────────────────────────

function FormSection({
  title,
  description,
  children,
  noBorder,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  noBorder?: boolean;
}) {
  return (
    <div className={`flex ${noBorder ? "" : "border-b border-(--color-border)"}`}>
      <div className="w-[300px] shrink-0 bg-(--color-surface-muted) border-r border-(--color-border) p-5 flex flex-col gap-2">
        <h3 className="text-sm font-medium text-(--color-text-primary)">{title}</h3>
        {description && (
          <p className="text-sm text-(--color-text-secondary) leading-snug">{description}</p>
        )}
      </div>
      <div className="flex-1 p-5 flex flex-col gap-4">{children}</div>
    </div>
  );
}

function FormField({
  label,
  id,
  error,
  children,
}: {
  label: string;
  id?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 flex-1">
      <Label htmlFor={id} className="text-sm font-medium text-(--color-text-primary)">
        {label}
      </Label>
      {children}
      {error && <p className="text-xs text-(--color-danger-text)">{error}</p>}
    </div>
  );
}

const inputClass = "";

// ─── Locations section ────────────────────────────────────────────────────────

function LocationsSection({
  selectedIds,
  companyId,
  onToggle,
}: {
  selectedIds: Set<number>;
  companyId?: number;
  onToggle: (id: number) => void;
}) {
  const { t } = useLingui();
  const [locationSearch, setLocationSearch] = useState("");
  const [debouncedSearch] = useDebounce(locationSearch, 300);

  return (
    <div className="flex flex-col gap-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-(--color-text-secondary)" />
        <Input
          placeholder={t`Search locations...`}
          value={locationSearch}
          onChange={(e) => setLocationSearch(e.target.value)}
          className={`${inputClass} pl-9`}
        />
      </div>

      {/* Assigned locations */}
      {selectedIds.size > 0 && (
        <AssignedLocations selectedIds={selectedIds} companyId={companyId} onToggle={onToggle} />
      )}

      {/* Available locations via virtual scroller */}
      <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto pr-1">
        <VirtualScroller<Location>
          queryKey={["locations", "picker", debouncedSearch, companyId]}
          queryFn={(page, pageSize) =>
            locationApi.paginate({ page, limit: pageSize, search: debouncedSearch || undefined, companyId })
          }
          pageSize={20}
          className="flex flex-col gap-2"
          emptyMessage={companyId ? t`No locations for this concept` : t`No unassigned locations`}
          renderSkeleton={() => (
            <div className="h-16 rounded-lg bg-(--color-surface-muted) animate-pulse" />
          )}
          renderItem={(loc) =>
            selectedIds.has(loc.id) ? null : (
              <LocationRow
                key={loc.id}
                location={loc}
                assigned={false}
                onToggle={() => onToggle(loc.id)}
              />
            )
          }
        />
      </div>
    </div>
  );
}

function AssignedLocations({
  selectedIds,
  companyId,
  onToggle,
}: {
  selectedIds: Set<number>;
  companyId?: number;
  onToggle: (id: number) => void;
}) {
  const { t } = useLingui();

  const { data } = useInfiniteQuery({
    queryKey: ["locations", "picker", "", companyId],
    queryFn: ({ pageParam }) => locationApi.paginate({ page: pageParam as number, limit: 20, companyId }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.hasNext ? last.meta.page + 1 : undefined),
  });

  const allFetched = data?.pages.flatMap((p) => p.data) ?? [];
  const selected = allFetched.filter((loc) => selectedIds.has(loc.id));

  if (selected.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-(--color-text-secondary) uppercase tracking-wide">
        {t`Assigned`} ({selected.length})
      </span>
      {selected.map((loc) => (
        <LocationRow
          key={loc.id}
          location={loc}
          assigned
          onToggle={() => onToggle(loc.id)}
        />
      ))}
    </div>
  );
}

function LocationRow({
  location,
  assigned,
  onToggle,
}: {
  location: Location;
  assigned: boolean;
  onToggle: () => void;
}) {
  const { t } = useLingui();
  return (
    <div className="flex items-center gap-3 bg-(--color-surface-muted) border border-(--color-border) rounded-lg p-3">
      <div className="size-10 rounded-[10px] bg-(--color-surface-accent) shrink-0 flex items-center justify-center">
        <MapPin className="size-4 text-(--color-text-secondary)" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-(--color-text-primary) truncate">{location.name}</p>
        <p className="text-xs text-(--color-text-secondary) truncate">
          {[location.code, location.city, location.timezone].filter(Boolean).join(" • ")}
        </p>
      </div>
      <button
        type="button"
        onClick={onToggle}
        className={
          assigned
            ? "text-xs font-medium text-(--color-danger-text) hover:underline shrink-0"
            : "text-xs font-medium text-(--color-text-primary) bg-(--color-surface-content) border border-(--color-border) rounded-md px-3 py-1.5 hover:bg-(--color-surface-muted) transition-colors shrink-0"
        }
      >
        {assigned ? t`Remove` : t`Add`}
      </button>
    </div>
  );
}

// ─── RegionFormDialog ─────────────────────────────────────────────────────────
// Pure controlled dialog. No trigger button — callers manage open state.

interface RegionFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RegionFormDialog({ open, onOpenChange }: RegionFormDialogProps) {
  const { t } = useLingui();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const idPrefix = useId();

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<number>>(new Set());
  const [companyName, setCompanyName] = useState("");
  const codeManuallyEdited = useRef(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<RegionFormValues>({
    resolver: zodResolver(regionFormSchema),
    mode: "onBlur",
    defaultValues: {
      name: "",
      description: "",
      isActive: true,
      companyId: undefined,
    },
  });

  const companyId = useWatch({ control, name: "companyId" });
  const companyIdNum = companyId ? Number(companyId) : undefined;
  const userOrgId = user?.organization?.id ?? undefined;

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("name", e.target.value, { shouldValidate: true });
  };

  const toggleLocation = (id: number) => {
    setSelectedLocationIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset();
      setSelectedLocationIds(new Set());
      setCompanyName("");
      setSubmitError(null);
      codeManuallyEdited.current = false;
    }
    onOpenChange(nextOpen);
  };

  const createMutation = useMutation({
    mutationFn: async (data: RegionFormValues) => {
      const code = data.name
        .toUpperCase()
        .replace(/\s+/g, "-")
        .replace(/[^A-Z0-9-]/g, "")
        .replace(/-{2,}/g, "-")
        .replace(/^-|-$/g, "");

      const region = await regionApi.create({
        name: data.name.trim(),
        code,
        isActive: data.isActive,
        ...(data.companyId && { companyId: Number(data.companyId) }),
      });

      if (selectedLocationIds.size > 0) {
        await Promise.all(
          Array.from(selectedLocationIds).map((locId) =>
            locationApi.update(String(locId), { regionId: region.id }),
          ),
        );
      }

      return region;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["regions"] });
      queryClient.invalidateQueries({ queryKey: ["locations"] });
      handleOpenChange(false);
      toast.success(t`Region created successfully`);
    },
    onError: (error: Error) => {
      setSubmitError(error.message || t`Failed to create region`);
    },
  });

  const isPending = createMutation.isPending;

  const onSubmit = (data: RegionFormValues) => {
    setSubmitError(null);
    createMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        hideClose
        className="max-w-[900px] p-0 overflow-hidden gap-0 border-none rounded-(--radius-lg)"
      >
        <div className="flex flex-col bg-(--color-surface-content) max-h-[90vh]">
          {/* Header */}
          <div className="flex items-start justify-between p-5 border-b border-(--color-border)">
            <div className="flex flex-col gap-1.5">
              <DialogTitle className="text-2xl font-medium text-(--color-text-primary) leading-none">
                {t`Add region`}
              </DialogTitle>
              <p className="text-base text-(--color-text-secondary) font-normal leading-none">
                {t`Create a new region and optionally assign locations to it.`}
              </p>
            </div>
            <DialogClose
              aria-label={t`Close dialog`}
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
            >
              <X className="h-6 w-6" />
            </DialogClose>
          </div>

          <form
            id={`${idPrefix}-form`}
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="flex flex-col flex-1 overflow-y-auto"
          >
            {/* General details */}
            <FormSection
              title={t`General details`}
              description={t`Basic information about this region.`}
            >
              <FormField
                label={t`Region name`}
                id={`${idPrefix}-name`}
                error={errors.name?.message}
              >
                <Input
                  id={`${idPrefix}-name`}
                  placeholder={t`Enter region name...`}
                  {...register("name")}
                  onChange={handleNameChange}
                  className={`${inputClass} ${errors.name ? "border-(--color-danger)" : ""}`}
                  autoFocus
                />
              </FormField>

              <div className="flex gap-3">
                <FormField label={t`Concept`} id={`${idPrefix}-company`}>
                  <VirtualSelectField<Company>
                    queryKeyBase={["region-dialog", "companies", userOrgId]}
                    buildQueryFn={(search) => (page, size) =>
                      companyApi.paginate({ page, limit: size, search, organizationId: userOrgId })
                    }
                    getItemValue={(c) => String(c.id)}
                    getItemLabel={(c) => c.name}
                    value={companyId ?? ""}
                    displayValue={companyName}
                    onChange={(value, company) => {
                      setValue("companyId", value);
                      setCompanyName(company.name);
                      setSelectedLocationIds(new Set());
                    }}
                    onClear={() => {
                      setValue("companyId", undefined);
                      setCompanyName("");
                      setSelectedLocationIds(new Set());
                    }}
                    placeholder={t`Select a concept...`}
                    searchPlaceholder={t`Search concepts…`}
                    emptyMessage={t`No concepts found`}
                  />
                </FormField>

                <FormField label={t`Status`}>
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value ? "active" : "inactive"}
                        onValueChange={(v) => field.onChange(v === "active")}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">{t`Active`}</SelectItem>
                          <SelectItem value="inactive">{t`Inactive`}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </FormField>
              </div>

              <FormField label={t`Description`} id={`${idPrefix}-description`}>
                <Textarea
                  id={`${idPrefix}-description`}
                  placeholder={t`Enter a description...`}
                  {...register("description")}
                  className="rounded-lg border-(--color-border) bg-(--color-surface-content) shadow-(--shadow-button) placeholder:text-(--color-text-secondary) text-sm min-h-[80px] resize-none"
                />
              </FormField>
            </FormSection>

            {/* Locations */}
            <FormSection
              title={t`Locations list`}
              description={t`Assign existing locations to this region.`}
              noBorder
            >
              <LocationsSection
                selectedIds={selectedLocationIds}
                companyId={companyIdNum}
                onToggle={toggleLocation}
              />
            </FormSection>
          </form>

          {/* Footer */}
          <div className="p-5 border-t border-(--color-border) flex flex-col gap-3 bg-(--color-surface-content) shrink-0">
            {submitError && (
              <div className="flex items-center gap-2 rounded-lg bg-(--color-danger)/10 border border-(--color-danger)/20 px-4 py-2.5 text-sm text-(--color-danger-text)">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{submitError}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <p className="text-sm text-(--color-text-secondary)">
                {t`Region code is auto-generated from the name`}
              </p>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  className="w-[200px] h-[52px] rounded-(--radius-food-thumb) border-(--color-border) text-base font-medium text-(--color-text-primary)"
                >
                  {t`Cancel`}
                </Button>
                <Button
                  form={`${idPrefix}-form`}
                  type="submit"
                  disabled={isPending}
                  className="w-[200px] h-[52px] rounded-(--radius-food-thumb) bg-(--color-action) text-(--color-action-fg) text-base font-medium hover:bg-(--color-action)/90 disabled:opacity-50"
                >
                  {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t`Save`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── CreateRegionDialog ───────────────────────────────────────────────────────
// Self-contained trigger button + dialog.

export function CreateRegionDialog() {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        className="h-11 gap-2 rounded-[10px] bg-(--color-action) hover:bg-(--color-action)/90 text-(--color-action-fg) text-sm font-medium"
        onClick={() => setOpen(true)}
      >
        <PlusCircle className="size-5" />
        {t`Add region`}
      </Button>
      <RegionFormDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
